import React, { useEffect, useMemo, useState } from "react";
import { UpdateManager } from "../services/UpdateManager";
import { getBuildMetaSync } from "../version/buildMeta";
import "./UpdateNotification.css";

const normalizeBuildId = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "unknown") return "";
  return s;
};

const parseBuildIdSortKey = (buildId) => {
  const s = String(buildId || "").trim();
  const m = /^(\d{8})-(\d{4})-/.exec(s);
  if (!m) return null;
  const key = Number(`${m[1]}${m[2]}`);
  return Number.isFinite(key) ? key : null;
};

const compareBuildIds = (a, b) => {
  const ak = parseBuildIdSortKey(a);
  const bk = parseBuildIdSortKey(b);
  if (ak != null && bk != null) {
    if (ak < bk) return -1;
    if (ak > bk) return 1;
    return 0;
  }
  const as = String(a || "");
  const bs = String(b || "");
  if (as < bs) return -1;
  if (as > bs) return 1;
  return 0;
};

const parseSemver = (v) => {
  const s = String(v || "").trim();
  const m = /^(\d+)\.(\d+)\.(\d+)/.exec(s);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};

const compareSemver = (a, b) => {
  const av = parseSemver(a);
  const bv = parseSemver(b);
  if (!av || !bv) return 0;
  for (let i = 0; i < 3; i += 1) {
    if (av[i] < bv[i]) return -1;
    if (av[i] > bv[i]) return 1;
  }
  return 0;
};

const formatReleaseDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const text = d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${text} IST`;
  } catch {
    return null;
  }
};

export default function UpdateNotification() {
  const [state, setState] = useState(UpdateManager.state);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const unsub = UpdateManager.subscribe(setState);
    UpdateManager.start().catch(() => {});
    return () => {
      unsub?.();
    };
  }, []);

  const phase = useMemo(() => {
    const p = typeof state?.phase === "string" ? state.phase : "";
    if (p) return p;
    // Backwards-compat fallback
    if (state?.status === "force_update") return "FORCE_UPDATE";
    if (state?.status === "update_available") return "UPDATE_AVAILABLE";
    return "IDLE";
  }, [state]);

  const isUpdating = phase === "UPDATING";

  useEffect(() => {
    // Ensure only one modal exists and it closes when the update UI is no longer active.
    const isPopup =
      phase === "UPDATE_AVAILABLE" || phase === "FORCE_UPDATE" || phase === "UPDATING";
    if (!isPopup && notesOpen) setNotesOpen(false);
  }, [phase, notesOpen]);

  const frontend = useMemo(() => {
    return state?.frontend || getBuildMetaSync();
  }, [state]);

  const backend = state?.backend && typeof state.backend === "object" ? state.backend : null;
  const backendBuildId = normalizeBuildId(backend?.backendBuildId);
  const frontendBuildId = normalizeBuildId(frontend?.buildId);

  const { latestMeta, earlierMeta } = useMemo(() => {
    const frontendMeta = {
      source: "frontend",
      version: frontend?.version || "0.0.0",
      buildId: frontendBuildId || "unknown",
    };
    const backendMeta = {
      source: "backend",
      version: backend?.version || "0.0.0",
      buildId: backendBuildId || "unknown",
    };

    // Prefer buildId ordering if possible; otherwise fall back to semver comparison.
    if (backendBuildId && frontendBuildId) {
      const cmp = compareBuildIds(frontendBuildId, backendBuildId);
      if (cmp >= 0) return { latestMeta: frontendMeta, earlierMeta: backendMeta };
      return { latestMeta: backendMeta, earlierMeta: frontendMeta };
    }

    const semverCmp = compareSemver(frontendMeta.version, backendMeta.version);
    if (semverCmp >= 0) return { latestMeta: frontendMeta, earlierMeta: backendMeta };
    return { latestMeta: backendMeta, earlierMeta: frontendMeta };
  }, [backend?.version, backendBuildId, frontend?.version, frontendBuildId]);

  const releaseNotes = Array.isArray(state?.releaseNotes) ? state.releaseNotes : [];
  const whatsNew = state?.whatsNew && typeof state.whatsNew === "object" ? state.whatsNew : null;
  const whatsNewEntry = whatsNew?.entry || null;
  const whatsNewFeatures =
    Array.isArray(whatsNewEntry?.features) ? whatsNewEntry.features.slice(0, 4) : [];

  const showWhatsNewStandalone =
    phase === "IDLE" &&
    whatsNew?.mode === "whats_new" &&
    Boolean(whatsNew?.version);

  const showUpdateBanner = phase === "UPDATE_AVAILABLE" || phase === "UPDATING";
  const showWhatsNewInUpdate =
    showUpdateBanner && whatsNew?.mode === "update_available" && Boolean(whatsNew?.version);

  if (!state?.isInitialized) return null;

  if (phase === "FORCE_UPDATE" || state?.status === "force_update") {
    return (
      <div
        className="force-update-modal__backdrop"
        role="dialog"
        aria-modal="true"
      >
        <div className="force-update-modal__card">
          <h2 className="force-update-modal__title">Application updated</h2>
          <p className="force-update-modal__sub">
            {state.message || "Please refresh to continue."}
          </p>
          {whatsNewEntry && (
            <>
              <ul className="update-banner__list">
                {whatsNewFeatures.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <div className="update-banner__actions">
                <button
                  type="button"
                  className="cancle-btn"
                  onClick={() => setNotesOpen(true)}
                >
                  View Full Release Notes
                </button>
              </div>
            </>
          )}
          <div className="force-update-modal__actions">
            <button
              type="button"
              className="custom-btn"
              onClick={() => UpdateManager.requestRefresh()}
              disabled={isUpdating}
            >
              {isUpdating ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div className="update-banner__meta">
            <div>
              Latest: v{latestMeta.version} ({latestMeta.buildId})
            </div>
            <div>
              Earlier: v{earlierMeta.version} ({earlierMeta.buildId})
            </div>
          </div>
        </div>
        {notesOpen && (
          <div
            className="release-notes-modal__backdrop"
            role="dialog"
            aria-modal="true"
            onClick={() => setNotesOpen(false)}
          >
            <div
              className="release-notes-modal__card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="release-notes-modal__header">
                <h3 className="release-notes-modal__title">Release Notes</h3>
                <button
                  type="button"
                  className="cancle-btn"
                  onClick={() => setNotesOpen(false)}
                >
                  Close
                </button>
              </div>
              <div className="release-notes-modal__body">
                {releaseNotes.length === 0 ? (
                  <p className="update-banner__sub">No release notes available.</p>
                ) : (
                  releaseNotes.slice(0, 2).map((item) => (
                    <div key={item.version} className="release-notes-item">
                      <p className="release-notes-item__v">
                        Version {item.version}
                        {item.title ? ` — ${item.title}` : ""}
                      </p>
                      <p className="release-notes-item__meta">
                        {formatReleaseDate(item.releasedAt) || ""}
                      </p>
                      {item.description ? (
                        <p className="release-notes-item__desc">{item.description}</p>
                      ) : null}
                      {Array.isArray(item.features) && item.features.length > 0 ? (
                        <ul className="update-banner__list">
                          {item.features.map((f) => (
                            <li key={`${item.version}:${f}`}>{f}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!showUpdateBanner && !showWhatsNewStandalone) return null;

  const refreshPending = Boolean(state?.refreshPending);
  const bannerTitle = showWhatsNewStandalone
    ? `What’s New in v${whatsNew?.version || frontend?.version || "0.0.0"}`
    : showWhatsNewInUpdate
      ? `Version ${whatsNew?.version} available`
      : isUpdating
        ? "Refreshing..."
        : "New version available";

  const bannerSub = showWhatsNewStandalone
    ? "Here are the latest improvements."
    : refreshPending
      ? "New version available. Refresh after response completes."
      : "Refresh to get latest improvements.";

  const onLater = () => {
    if (showWhatsNewStandalone && whatsNew?.version) {
      UpdateManager.dismissWhatsNew(whatsNew.version);
      return;
    }
    if (showWhatsNewInUpdate && whatsNew?.version) {
      UpdateManager.dismissWhatsNew(whatsNew.version);
    }
    UpdateManager.dismiss();
  };

  const onPrimary = () => {
    if (isUpdating) return;
    if (showWhatsNewStandalone && whatsNew?.version) {
      UpdateManager.setWhatsNewSeen(whatsNew.version);
      return;
    }
    UpdateManager.requestRefresh();
    if (showWhatsNewInUpdate && whatsNew?.version) {
      UpdateManager.setWhatsNewSeen(whatsNew.version);
    }
  };

  return (
    <>
      <div className="update-banner" role="status" aria-live="polite">
        <div className="update-banner__bar" />
        <div className="update-banner__body">
          <p className="update-banner__title">{bannerTitle}</p>
          <p className="update-banner__sub">{bannerSub}</p>

          {(showWhatsNewStandalone || showWhatsNewInUpdate) && whatsNewEntry && (
            <ul className="update-banner__list">
              {whatsNewFeatures.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          )}

          <div className="update-banner__meta">
            {showUpdateBanner ? (
              <>
                <div>
                  Latest: v{latestMeta.version} ({latestMeta.buildId})
                </div>
                <div>
                  Earlier: v{earlierMeta.version} ({earlierMeta.buildId})
                </div>
              </>
            ) : (
              <div>
                Current: v{frontend?.version || "0.0.0"} ({frontend?.buildId || "unknown"})
              </div>
            )}
          </div>

          <div className="update-banner__actions">
            <button
              type="button"
              className={`custom-btn ${showWhatsNewStandalone ? "btn-dark" : "btn-dark"} `}
              onClick={onPrimary}
              disabled={isUpdating}
            >
              {showWhatsNewStandalone ? "Got it" : isUpdating ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="cancle-btn"
              onClick={onLater}
              disabled={isUpdating}
            >
              Later
            </button>
            {(showWhatsNewStandalone || showWhatsNewInUpdate) && (
              <button
                type="button"
                className="cancle-btn"
                onClick={() => setNotesOpen(true)}
                disabled={isUpdating}
              >
                View Full Release Notes
              </button>
            )}
          </div>
        </div>
      </div>

      {notesOpen && (
        <div
          className="release-notes-modal__backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setNotesOpen(false)}
        >
          <div
            className="release-notes-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="release-notes-modal__header">
              <h3 className="release-notes-modal__title">Release Notes</h3>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setNotesOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="release-notes-modal__body">
              {releaseNotes.length === 0 ? (
                <p className="update-banner__sub">No release notes available.</p>
              ) : (
                releaseNotes.slice(0, 2).map((item) => (
                  <div key={item.version} className="release-notes-item">
                    <p className="release-notes-item__v">
                      Version {item.version}
                      {item.title ? ` — ${item.title}` : ""}
                    </p>
                    <p className="release-notes-item__meta">
                      {formatReleaseDate(item.releasedAt) || ""}
                    </p>
                    {item.description ? (
                      <p className="release-notes-item__desc">{item.description}</p>
                    ) : null}
                    {Array.isArray(item.features) && item.features.length > 0 ? (
                      <ul className="update-banner__list">
                        {item.features.map((f) => (
                          <li key={`${item.version}:${f}`}>{f}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
